import * as XLSX from 'xlsx';

// Tree node shape returned by tradingAccount.getTree with includeMetadata: true.
// Kept locally instead of importing from the server router to avoid a
// frontend-on-server type dependency for a leaf utility.
export interface ExportableAccountNode {
  id: string;
  code: string;
  name: string;
  type: string;
  balanceType: string;
  parentCode: string | null;
  balance: number;
  currency: string;
  isActive: boolean;
  isSystemAccount: boolean;
  description?: string | null;
  createdAt?: Date | string;
  updatedAt?: Date | string;
  children: ExportableAccountNode[];
}

const TDMS_TYPE_LABELS: Record<string, string> = {
  CURRENT_ASSET: 'Donen Varliklar',
  FIXED_ASSET: 'Duran Varliklar',
  SHORT_TERM_LIABILITY: 'Kisa Vadeli Yab. Kaynaklar',
  LONG_TERM_LIABILITY: 'Uzun Vadeli Yab. Kaynaklar',
  EQUITY: 'Oz Kaynaklar',
  INCOME: 'Gelirler',
  COST: 'Giderler/Maliyetler',
  FREE: 'Serbest',
  OFF_BALANCE: 'Nazim Hesaplar',
};

interface ExportRow {
  Code: string;
  Name: string;
  Type: string;
  'Balance Type': string;
  'Parent Code': string;
  Balance: number;
  Currency: string;
  Active: string;
  'System Account': string;
  Description: string;
  'Created At': string;
  'Updated At': string;
}

function formatDate(value: Date | string | undefined): string {
  if (!value) return '';
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

// Depth-first flatten preserving TDMS code order. Each row carries its depth
// so the caller can indent the Name column.
function flatten(
  nodes: ExportableAccountNode[],
  depth: number,
): Array<{ node: ExportableAccountNode; depth: number }> {
  const out: Array<{ node: ExportableAccountNode; depth: number }> = [];
  for (const node of nodes) {
    out.push({ node, depth });
    if (node.children.length > 0) {
      out.push(...flatten(node.children, depth + 1));
    }
  }
  return out;
}

function buildRow(
  node: ExportableAccountNode,
  depth: number,
  indented: boolean,
): ExportRow {
  const namePrefix = indented ? '  '.repeat(depth) : '';
  return {
    Code: node.code,
    Name: `${namePrefix}${node.name}`,
    Type: TDMS_TYPE_LABELS[node.type] ?? node.type,
    'Balance Type': node.balanceType,
    'Parent Code': node.parentCode ?? '',
    Balance: Number(node.balance) || 0,
    Currency: node.currency,
    Active: node.isActive ? 'Yes' : 'No',
    'System Account': node.isSystemAccount ? 'Yes' : 'No',
    Description: node.description ?? '',
    'Created At': formatDate(node.createdAt),
    'Updated At': formatDate(node.updatedAt),
  };
}

export interface ExportOptions {
  indented: boolean;
  filename: string;
}

export function exportChartOfAccounts(
  tree: ExportableAccountNode[],
  opts: ExportOptions,
): void {
  const flat = flatten(tree, 0);
  const rows: ExportRow[] = flat.map(({ node, depth }) =>
    buildRow(node, depth, opts.indented),
  );

  const worksheet = XLSX.utils.json_to_sheet(rows);

  // Column widths tuned to keep the file readable when opened in Excel
  worksheet['!cols'] = [
    { wch: 18 }, // Code
    { wch: 40 }, // Name
    { wch: 22 }, // Type
    { wch: 14 }, // Balance Type
    { wch: 16 }, // Parent Code
    { wch: 16 }, // Balance
    { wch: 8 },  // Currency
    { wch: 8 },  // Active
    { wch: 8 },  // System Account
    { wch: 40 }, // Description
    { wch: 20 }, // Created At
    { wch: 20 }, // Updated At
  ];

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Chart of Accounts');
  XLSX.writeFile(workbook, opts.filename);
}

export function todayFilenameStamp(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
